import React, { Fragment } from 'react';
import { Path } from '@react-pdf/renderer';
import { values } from 'lodash';

/**
 * Interface for storage dimensions
 */
interface StorageDimensions {
  beam: number;
  depth: number;
}

/**
 * Interface for area constants
 */
interface AreaConstants {
  storage: StorageDimensions;
  [key: string]: any;
}

/**
 * Interface for constants
 */
interface Constants {
  area: AreaConstants;
  [key: string]: any;
}

/**
 * Interface for rack arrays
 */
interface RackArrays {
  xArray: number[];
  yArray: number[];
}

/**
 * Interface for area location
 */
interface AreaLocation {
  left: number;
  top: number;
}

/**
 * Interface for processed area
 */
interface ProcessedArea extends AreaLocation {
  id: string;
  racks?: RackArrays;
  ver: boolean;
}

/**
 * Interface for area
 */
interface Area {
  id: string;
  location: AreaLocation;
  rackArrays?: RackArrays;
  storageOrientation?: string;
  [key: string]: any;
}

/**
 * Interface for areas object
 */
interface Areas {
  [key: string]: Area;
}

/**
 * Interface for content
 */
interface Content {
  areas: Areas;
  [key: string]: any;
}

/**
 * Interface for rack path data
 */
interface RackPath {
  d: string;
}

/**
 * Interface for component props
 */
interface MuiStorageProps {
  inContainer?: boolean;
  areaId?: string;
  scale: number;
  content: Content;
  constants: Constants;
}

/**
 * MUI Storage component that replaces the styled-components Storage component
 * 
 * This component provides a Material UI equivalent of the original Storage component
 * with similar props and functionality. It's used for PDF generation.
 * 
 * @param {MuiStorageProps} props
 * @returns {JSX.Element} MUI Storage component
 */
const MuiStorage: React.FC<MuiStorageProps> = (props) => {
  const { inContainer, areaId, scale, content: { areas }, constants: { area: { storage } } } = props;
  
  // Process areas to get rack information
  const areaArray: ProcessedArea[] = (areaId ? [areas?.[areaId]].filter(Boolean) : values(areas)).map((area: Area) => ({
    id: area.id,
    ...area.location,
    racks: area.rackArrays,
    ver: area.storageOrientation === 'horizontal'
  }));

  // Add the storage racks
  const { beam, depth } = storage;
  const shortSide = depth * scale; // 48"
  const longSide = beam * scale; // 96"

  const racks: RackPath[] = areaArray.flatMap(area => {
    const x0 = 2 + (inContainer ? area.left * scale : 0);
    const y0 = 2 + (inContainer ? area.top * scale : 0);
    const xArray = area.racks?.xArray || [];
    const yArray = area.racks?.yArray || [];
    const ver = area.ver;
    
    return yArray.flatMap((y, i) =>
      xArray.flatMap((x, j) => ({
        d: `m${x0 + x * scale} ${y0 + y * scale} h${ver ? longSide : shortSide}v${ver ? shortSide : longSide}h${-(ver ? longSide : shortSide)}z`
      }))
    );
  });

  return (
    <Fragment>
      {racks.map((rack, index) => <Path key={`rack-${index}`} d={rack.d} stroke="#a5aaad" />)}
    </Fragment>
  );
};

export default MuiStorage;
